import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Calendar as CalendarIcon, Clock, Loader2, CheckCircle2, X, ArrowLeft, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatSlotTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatLongDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

// Build list of next N weekdays starting from tomorrow (or the day after if after hours).
const buildUpcomingDates = (count = 10) => {
  const out = [];
  let cursor = new Date();
  cursor.setDate(cursor.getDate() + 1);
  while (out.length < count) {
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) {
      out.push(new Date(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

export const BookingModal = ({ isOpen, onClose, defaults = {}, strategySessionId = null }) => {
  const [step, setStep] = useState(1); // 1=date, 2=time, 3=details, 4=done
  const [dateList] = useState(() => buildUpcomingDates(10));
  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({
    name: defaults.name || '',
    email: defaults.email || '',
    phone: '',
    company: defaults.company || '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  // Reset when reopened
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedDate(null);
      setSlots([]);
      setSelectedSlot(null);
      setError('');
      setConfirmation(null);
      setForm((f) => ({ ...f, name: defaults.name || f.name, email: defaults.email || f.email, company: defaults.company || f.company }));
    }
  }, [isOpen, defaults.name, defaults.email, defaults.company]);

  // Fetch availability when date is picked
  useEffect(() => {
    if (!selectedDate) return;
    let cancelled = false;
    const load = async () => {
      setLoadingSlots(true);
      setError('');
      try {
        const { data } = await axios.get(`${API}/bookings/availability`, {
          params: { date_str: selectedDate },
        });
        if (!cancelled) setSlots(data.slots || []);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || 'Could not load time slots.');
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  const canSubmit = useMemo(
    () => form.name.trim() && form.email.trim() && selectedSlot,
    [form, selectedSlot],
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/bookings/create`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        trade: defaults.trade || null,
        notes: form.notes.trim() || null,
        slot: selectedSlot,
        strategySessionId,
      });
      setConfirmation(data);
      setStep(4);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Booking failed. Try another slot.');
    } finally {
      setSubmitting(false);
    }
  };

  const pickDate = (d) => {
    setSelectedDate(toLocalDateStr(d));
    setSelectedSlot(null);
    setStep(2);
  };

  const pickSlot = (iso) => {
    setSelectedSlot(iso);
    setStep(3);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="bg-[#0a0a0a] border border-[#00E599]/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="booking-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-headline font-bold flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-[#00E599]" />
            Book your strategy call
          </DialogTitle>
          <p className="text-sm text-[#A1A1AA] font-body">
            Free 60 minutes. America/Phoenix (AZ / MST). Mon–Fri, 9 AM – 5 PM.
          </p>
        </DialogHeader>

        {/* Progress */}
        {step < 4 && (
          <div className="flex items-center gap-2 text-xs font-body text-[#A1A1AA] mb-2">
            <span className={step >= 1 ? 'text-[#00E599]' : ''}>1 · Date</span>
            <span>→</span>
            <span className={step >= 2 ? 'text-[#00E599]' : ''}>2 · Time</span>
            <span>→</span>
            <span className={step >= 3 ? 'text-[#00E599]' : ''}>3 · Your info</span>
          </div>
        )}

        {/* STEP 1: DATE */}
        {step === 1 && (
          <div className="space-y-3" data-testid="booking-step-date">
            <p className="text-sm text-[#A1A1AA] font-body">
              Pick a weekday in the next {dateList.length} business days:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {dateList.map((d) => {
                const iso = toLocalDateStr(d);
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => pickDate(d)}
                    data-testid={`booking-date-${iso}`}
                    className="p-3 rounded-lg border border-zinc-800 bg-[#050505] hover:border-[#00E599]/60 hover:bg-[#00E599]/5 transition-all text-left"
                  >
                    <div className="text-[10px] text-[#A1A1AA] font-body uppercase tracking-wider">
                      {d.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-lg font-headline font-bold text-white">
                      {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2: TIME */}
        {step === 2 && selectedDate && (
          <div className="space-y-4" data-testid="booking-step-time">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-[#A1A1AA] font-body">Selected date</div>
                <div className="text-lg font-headline font-bold">
                  {formatLongDate(selectedDate)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs text-[#A1A1AA] hover:text-[#00E599] font-body flex items-center gap-1"
                data-testid="booking-back-to-date"
              >
                <ArrowLeft className="w-3 h-3" />
                Change date
              </button>
            </div>

            {loadingSlots ? (
              <div className="flex items-center gap-2 py-8 justify-center text-[#A1A1AA] font-body">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading available times…
              </div>
            ) : slots.length === 0 ? (
              <div className="text-center py-8 text-[#A1A1AA] font-body" data-testid="booking-no-slots">
                No open slots this day. Please pick another.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {slots.map((iso) => (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => pickSlot(iso)}
                    data-testid={`booking-slot-${iso}`}
                    className="py-3 rounded-lg border border-zinc-800 bg-[#050505] hover:border-[#00E599] hover:bg-[#00E599]/10 font-body font-semibold text-white transition-all"
                  >
                    <Clock className="w-3 h-3 inline mr-1 text-[#00E599]" />
                    {formatSlotTime(iso)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 3: DETAILS */}
        {step === 3 && selectedSlot && (
          <div className="space-y-4" data-testid="booking-step-details">
            <div className="glass p-4 rounded-lg border border-[#00E599]/30 flex items-center justify-between">
              <div>
                <div className="text-xs text-[#A1A1AA] font-body">Your slot</div>
                <div className="text-base font-headline font-bold">
                  {formatLongDate(selectedSlot)} at {formatSlotTime(selectedSlot)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="text-xs text-[#A1A1AA] hover:text-[#00E599] font-body"
                data-testid="booking-back-to-time"
              >
                Change
              </button>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Full name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#050505] border-zinc-800 text-white"
                placeholder="John Smith"
                data-testid="booking-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-[#050505] border-zinc-800 text-white"
                placeholder="you@company.com"
                data-testid="booking-email-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-white">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-[#050505] border-zinc-800 text-white"
                  placeholder="(602) 555-0001"
                  data-testid="booking-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Company</Label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="bg-[#050505] border-zinc-800 text-white"
                  placeholder="Smith HVAC"
                  data-testid="booking-company-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Anything we should know? <span className="text-[#A1A1AA] font-normal">(optional)</span></Label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 bg-[#050505] border border-zinc-800 rounded-lg text-white font-body focus:outline-none focus:border-[#00E599] resize-none"
                placeholder="Biggest bottleneck, best time to call, etc."
                data-testid="booking-notes-input"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm font-body" data-testid="booking-error">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="w-full bg-[#00E599] hover:bg-[#00C985] text-[#050505] font-bold py-6 disabled:opacity-50"
              data-testid="booking-confirm-btn"
            >
              {submitting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Confirming…
                </span>
              ) : (
                'Confirm booking'
              )}
            </Button>
          </div>
        )}

        {/* STEP 4: DONE */}
        {step === 4 && confirmation && (
          <div className="text-center py-6" data-testid="booking-success">
            <div className="w-20 h-20 bg-[#00E599]/15 border border-[#00E599]/40 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-[#00E599]" />
            </div>
            <h3 className="text-2xl font-headline font-bold mb-2">You're on the calendar.</h3>
            <p className="text-[#A1A1AA] font-body mb-6">
              We'll send a calendar invite + a confirmation text from <span className="text-[#00E599] font-semibold">(602) 730-6385</span> shortly.
            </p>

            <div className="glass p-5 rounded-xl border border-[#00E599]/30 text-left inline-block min-w-[280px]">
              <div className="text-xs text-[#A1A1AA] font-body mb-1">Your strategy call</div>
              <div className="text-lg font-headline font-bold text-white mb-3">
                {formatLongDate(confirmation.slot)}
              </div>
              <div className="flex items-center gap-2 text-[#00E599] font-body font-semibold mb-2">
                <Clock className="w-4 h-4" />
                {formatSlotTime(confirmation.slot)} America/Phoenix
              </div>
              <div className="text-xs text-[#A1A1AA] font-body">
                Confirmation ID: <span className="font-mono">{confirmation.id.slice(0, 8)}</span>
              </div>
            </div>

            <div className="mt-8">
              <Button
                onClick={onClose}
                variant="outline"
                className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900"
                data-testid="booking-close-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
